/**
 * Checkout, for whichever provider this deployment has configured.
 *
 * Two exist because of where the business is: Stripe does not onboard sellers
 * in every country, Paddle acts as merchant of record and does. The server
 * decides which is live — the dashboard asks and adapts, so neither provider
 * is named at the call sites.
 *
 * The flows are genuinely different, not two spellings of one thing. Stripe
 * hands back a URL and the browser leaves the page. Paddle opens an overlay on
 * top of the dashboard and reports back through a callback, so nothing
 * navigates and the page has to be told when it is done.
 */

/**
 * A plan id, as the plans table spells it.
 *
 * Not a union of the ones that exist today. The paywall renders from the plan
 * catalogue the server sends, so a union here would mean every new tier needed
 * a code change in a file that has nothing to do with tiers -- and the failure
 * would be a button the server offers and this refuses to open.
 */
export type Plan = string;

export interface PaddleConfig {
  client_token: string | null;
  environment: "sandbox" | "production";
  /** Plan id to provider price id, for the plans that have one configured. */
  prices: Record<string, string | null | undefined>;
  /** An existing Paddle customer, or the email to prefill for a new one. */
  customer: { id: string } | { email: string };
  custom_data: { user_id: string };
}

/**
 * A checkout for a transaction the server already created, which is how the
 * Chrome extension buys: the server chose the prices, and this only opens it.
 */
export interface TransactionCheckout {
  transactionId: string;
  settings?: { displayMode?: "overlay"; theme?: "dark" | "light" };
}

export interface CheckoutPayload {
  items: { priceId: string; quantity: number }[];
  customer: { id: string } | { email: string };
  customData: { user_id: string };
  settings: {
    displayMode: "overlay";
    theme: "dark";
    /**
     * Omitted unless the page's own domain is one Paddle has approved for
     * checkout. Paddle refuses an unapproved return domain outright —
     * `transaction_checkout_url_domain_is_not_approved` — and that includes
     * localhost, so passing one unconditionally broke every checkout in
     * development, and would have broken production the day the dashboard
     * moved to a subdomain nobody had separately approved.
     */
    successUrl?: string;
    allowLogout: boolean;
  };
}

/**
 * Paddle is used only when it can actually open a checkout. The config
 * endpoint answers 404 when the provider is switched off, and it can also
 * answer successfully with a null token on a half-configured deployment —
 * both have to fall back rather than throw, or a misconfigured server shows
 * the streamer a stack trace instead of a Subscribe button.
 */
export function providerFor(cfg: PaddleConfig | null | undefined): "paddle" | "stripe" {
  return cfg && cfg.client_token ? "paddle" : "stripe";
}

export function paddlePriceFor(cfg: PaddleConfig, plan: Plan): string | null {
  const id = cfg.prices?.[plan];
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * `customData` is what the webhook reads to decide whose account a payment
 * belongs to, and it is sent from the browser, so in principle a streamer
 * could attribute their own purchase to someone else's account. That is a way
 * to give a subscription away, not a way to get one: the charge still has to
 * clear first. Paddle's documented flow for a new subscription is this one, and
 * the alternative — minting a transaction server-side — buys nothing here.
 */
export function checkoutPayload(
  cfg: PaddleConfig,
  plan: Plan,
  successUrl?: string,
): CheckoutPayload {
  const priceId = paddlePriceFor(cfg, plan);
  if (!priceId) {
    throw new Error(
      `The ${plan} plan isn't set up on this server yet. Email billing@stream-cut.com.`,
    );
  }
  return {
    items: [{ priceId, quantity: 1 }],
    customer: cfg.customer,
    customData: cfg.custom_data,
    settings: {
      displayMode: "overlay",
      theme: "dark",
      ...(successUrl ? { successUrl } : {}),
      // The email is already known and prefilled. Letting it be changed here
      // would produce a Paddle customer that no account matches.
      allowLogout: false,
    },
  };
}

/**
 * Where Paddle would send the browser after a successful payment.
 *
 * Only usable on a domain Paddle has approved for checkout, which is why
 * nothing passes it by default. Kept because a deployment that has been through
 * that approval may prefer the redirect, and the shape of the URL is the part
 * worth not getting wrong: back to the account page with a marker, rather than
 * a dedicated thank-you route. The subscription is not active the instant
 * payment clears — it becomes active when Paddle's webhook arrives — and the
 * account page is the one screen that can re-check and say so.
 */
export function successUrlFor(origin: string): string {
  return `${origin.replace(/\/+$/, "")}/#/account?subscribed=1`;
}

// --- Paddle.js ------------------------------------------------------------

const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

export interface CheckoutEvent {
  name?: string;
  data?: unknown;
}

interface PaddleJs {
  Initialize(o: { token: string; eventCallback?: (e: CheckoutEvent) => void }): void;
  Environment?: { set(env: string): void };
  Checkout: { open(o: CheckoutPayload | TransactionCheckout): void };
}

type WinWithPaddle = Window & { Paddle?: PaddleJs };

/** Initialize is not idempotent in Paddle.js v2; calling it twice warns and
 *  re-registers the callback. The dashboard is a single page, so a streamer
 *  who closes the overlay and reopens it would hit that. */
let initialised = false;

/**
 * Paddle takes exactly one event callback, at Initialize, which runs once for
 * the life of the page — so it cannot be the subscriber itself. It dispatches
 * to this set instead, which views can join and leave as they mount.
 */
const listeners = new Set<(e: CheckoutEvent) => void>();

/**
 * Subscribe to overlay events. Returns an unsubscribe function.
 *
 * The two that matter are `checkout.completed` — payment cleared, though the
 * subscription is not active until the webhook lands — and `checkout.closed`,
 * which is how a streamer who changed their mind is distinguished from one who
 * paid.
 */
export function onCheckoutEvent(fn: (e: CheckoutEvent) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function injectScript(src: string, win: WinWithPaddle): Promise<void> {
  return new Promise((resolve, reject) => {
    const doc = win.document;
    const existing = doc.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Paddle.js failed to load")), {
        once: true,
      });
      return;
    }
    const el = doc.createElement("script");
    el.src = src;
    el.async = true;
    el.addEventListener("load", () => resolve(), { once: true });
    el.addEventListener("error", () => reject(new Error("Paddle.js failed to load")), {
      once: true,
    });
    doc.head.append(el);
  });
}

export interface LoadDeps {
  win?: WinWithPaddle;
  loadScript?: (src: string, win: WinWithPaddle) => Promise<void>;
}

/**
 * Loaded on demand rather than from index.html. Most people who open the
 * dashboard never touch billing, and a third-party script on every page load
 * is a tracking surface and a render-blocking request for nothing.
 */
export async function loadPaddle(cfg: PaddleConfig, deps: LoadDeps = {}): Promise<PaddleJs> {
  const win = deps.win ?? (window as WinWithPaddle);
  const load = deps.loadScript ?? injectScript;

  if (!win.Paddle) await load(PADDLE_JS, win);
  const P = win.Paddle;
  if (!P) throw new Error("Paddle.js loaded but did not register.");

  if (!initialised) {
    // Order matters: the environment has to be set before Initialize, or the
    // token is validated against production and a sandbox token is rejected.
    if (cfg.environment === "sandbox") P.Environment?.set("sandbox");
    if (!cfg.client_token) throw new Error("No Paddle client token configured.");
    P.Initialize({
      token: cfg.client_token,
      // A listener that throws must not take the others down with it, and must
      // not surface inside Paddle.js as a checkout failure.
      eventCallback: (e) => {
        for (const fn of listeners) {
          try {
            fn(e);
          } catch (err) {
            console.error("checkout event listener failed", err);
          }
        }
      },
    });
    initialised = true;
  }
  return P;
}

/** Test seam: the module-level guard above would otherwise leak between tests. */
export function resetPaddleForTests(): void {
  initialised = false;
}

// --- Orchestration --------------------------------------------------------

/** Injected rather than imported so this module stays loadable on its own. */
export type ApiFn = <T>(method: string, path: string, body?: unknown) => Promise<T>;

/**
 * Navigation is returned, never performed, so the two providers' very
 * different endings stay describable by one type and the call sites do not
 * each have to know which one they are in.
 */
export type CheckoutResult = { mode: "overlay" } | { mode: "redirect"; url: string };

export interface BillingDeps {
  api: ApiFn;
  /**
   * Only for a deployment whose domain Paddle has approved for checkout.
   * Left unset, the overlay reports completion through `onCheckoutEvent`
   * instead of navigating, which works on any origin — localhost included.
   */
  successUrl?: string;
  /** Overridden in tests; defaults to the real Paddle.js path. */
  load?: (cfg: PaddleConfig) => Promise<PaddleJs>;
}

/**
 * Null means "not Paddle", for every reason it could be.
 *
 * The endpoint 404s when the provider is off, but a server that is down, a
 * token that has expired mid-session or a response of an unexpected shape all
 * have to land in the same place: falling back to Stripe and letting that
 * fail loudly is better than a dead button, because on a Stripe deployment
 * this call failing is the *expected* case.
 */
export async function fetchPaddleConfig(api: ApiFn): Promise<PaddleConfig | null> {
  try {
    const cfg = await api<PaddleConfig>("GET", "/v1/billing/paddle-config");
    return providerFor(cfg) === "paddle" ? cfg : null;
  } catch {
    return null;
  }
}

export async function startCheckout(plan: Plan, deps: BillingDeps): Promise<CheckoutResult> {
  const cfg = await fetchPaddleConfig(deps.api);

  if (cfg) {
    const payload = checkoutPayload(cfg, plan, deps.successUrl);
    const P = await (deps.load ?? ((c: PaddleConfig) => loadPaddle(c)))(cfg);
    P.Checkout.open(payload);
    return { mode: "overlay" };
  }

  const res = await deps.api<{ url: string }>("POST", "/v1/billing/checkout", { plan });
  return { mode: "redirect", url: res.url };
}

/**
 * Cancellation, card updates and invoices, on whichever provider holds the
 * subscription. Both are hosted pages — handling card details ourselves would
 * be a compliance burden with no upside.
 */
export async function manageBilling(deps: BillingDeps): Promise<CheckoutResult> {
  const cfg = await fetchPaddleConfig(deps.api);
  const path = cfg ? "/v1/billing/paddle-portal" : "/v1/billing/portal";
  const res = await deps.api<{ url: string }>("POST", path);
  return { mode: "redirect", url: res.url };
}
